import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '@chilipiper/api-client/src/context'
import { defaultErrorCallback } from '@chilipiper/service/src/api'
import { ApiError } from '@chilipiper/service/src/types'
import { RevisedOrgBranding } from '@chilipiper/api-client/src/workspace'

const getWorkspaceTenantDetailsKey = (tenantId: string) => ['workspace-tenant-details', tenantId]
export const useTenantDetails = (tenantId: string) => {
  const { workspaceV3 } = useApiClient()

  return useQuery({
    queryKey: getWorkspaceTenantDetailsKey(tenantId),
    queryFn: () => workspaceV3.guestGetOrgBrandingV3(tenantId),
  })
}
export const useUpdateTenantDetails = (tenantId: string) => {
  const { workspaceV3 } = useApiClient()
  const queryClient = useQueryClient()
  const { data: tenantDetails } = useTenantDetails(tenantId)

  return useMutation({
    mutationFn: (payload: { name: string }) => {
      if (!tenantDetails) {
        throw new Error('Tenant details not loaded yet')
      }
      return workspaceV3.updateTenantBrandingV3(
        tenantId,
        { ...tenantDetails.payload, companyName: payload.name },
        {
          // These double quotes are required in BE validation
          headers: { 'If-Match': `"${tenantDetails.metadata.revision}"` },
        }
      )
    },

    onError: error => defaultErrorCallback(error as unknown as ApiError),
    onMutate: async ({ name }) => {
      await queryClient.cancelQueries({ queryKey: getWorkspaceTenantDetailsKey(tenantId) })
      queryClient.setQueryData<RevisedOrgBranding>(
        getWorkspaceTenantDetailsKey(tenantId),
        previousData => {
          if (!previousData) {
            return
          }
          return {
            payload: { ...previousData.payload, companyName: name },
            metadata: { ...previousData.metadata, revision: previousData.metadata.revision + 1 },
          }
        }
      )
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: getWorkspaceTenantDetailsKey(tenantId) }),
  })
}
